import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

// One-time post-signup nudge: "your public profile lives at
// globaleyes.tv/{username}". Mounted in AppShell so it fires
// uniformly across every auth surface (email, magic, Google, Apple,
// SMS, X, QR) — every flow funnels through AuthContext setting
// `user`, so we just watch that and check a localStorage gate.
//
// Why a sticky toast and not a dialog?
// - Brand-new users may also see the InstallPromptDialog 2.5s in.
//   A second modal would mean stacked centered dialogs (bad UX).
//   A sticky toast (bottom-right by default) coexists peacefully
//   with whatever is open.
// - The user can dismiss with one tap and we never bug them again.
//
// Storage key is versioned (`_v1`) so we can re-fire on a future
// product change without colliding with the old key.
const STORAGE_KEY = "globaleyes_profile_share_toast_seen_v1";

const FirstLoginShareToast = () => {
  const { user } = useAuth();
  const firedFor = useRef(null);

  useEffect(() => {
    if (!user?.username) return;
    // Per-tab guard: in dev with React StrictMode the effect runs
    // twice; without this we'd double-fire the toast.
    if (firedFor.current === user.username) return;
    let seenMap;
    try { seenMap = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); }
    catch { seenMap = {}; }
    if (seenMap[user.username]) return;

    firedFor.current = user.username;
    // Wait a beat so the registration success toast lands first.
    const t = setTimeout(() => {
      // Embed UTMs in the SHARED profile URL so when the producer's
      // X followers click through, the XAttributionBeacon in AppShell
      // fires /x/attribution/log and the admin X Funnel panel can
      // bucket the conversions:
      //   list=user_share        — one shared bucket for ALL organic shares
      //   utm_campaign=first_share — separates this nudge from admin invites
      //   inv={username}         — who sent them; lets us thank top sharers
      const shareQs = `utm_source=x&utm_campaign=first_share&list=user_share&inv=${encodeURIComponent(user.username)}`;
      const profileUrl = `${window.location.origin}/${user.username}`;
      const trackedUrl = `${profileUrl}?${shareQs}`;
      const shareText = `I just claimed @${user.username} on GlobalEyes.TV — short videos, made by humans, not AI. Watch mine here:`;
      const xIntent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(trackedUrl)}&via=GlobalEyesDOTtv&hashtags=GlobalEyes`;

      toast.message(
        `Your public profile is live`,
        {
          duration: Infinity,
          description: `globaleyes.tv/${user.username} — share it to seed your first viewers.`,
          action: {
            label: "Share on X",
            onClick: () => {
              window.open(xIntent, "_blank", "noopener,noreferrer");
              try {
                seenMap[user.username] = Date.now();
                localStorage.setItem(STORAGE_KEY, JSON.stringify(seenMap));
              } catch { /* private mode — accept the re-prompt on next session */ }
            },
          },
          cancel: {
            label: "Later",
            onClick: () => {
              try {
                seenMap[user.username] = Date.now();
                localStorage.setItem(STORAGE_KEY, JSON.stringify(seenMap));
              } catch { /* ignore */ }
            },
          },
        },
      );
      // Persist the "seen" flag the moment we render the toast so
      // a hard reload mid-toast doesn't cause it to fire again on
      // the next mount. Users who never interacted will not get a
      // second nudge — that's intentional; we don't want to be the
      // app that re-pesters.
      try {
        seenMap[user.username] = Date.now();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(seenMap));
      } catch { /* ignore */ }
    }, 1500);

    return () => clearTimeout(t);
  }, [user?.username]);

  return null;
};

export default FirstLoginShareToast;
